"use client";

import Link from "next/link";
import React from "react";
import { twMerge } from "tailwind-merge";

import { withClickAnalytics } from "../withAnalytics";

import { LoadingSpinner } from "./spinners/LoadingSpinner";
import { getVariantStyles } from "./utils/buttonHelpers";

export type ButtonColor = "primary" | "secondary";
export type ButtonVariant = "3d" | "flat" | "outline" | "ghost" | "text";

export interface ButtonProps {
  analyticsId?: string;
  as?: string;
  analyticsProps?: Record<string, string | number | boolean>;
  ariaLabel?: string;
  children?: React.ReactNode;
  topClassName?: string;
  className?: string;
  disabled?: boolean;
  href?: string;
  fullWidth?: boolean;
  leftIcon?: React.ReactNode;
  loading?: boolean;
  loadingLabel?: string;
  onClick?: (event: any) => void | Promise<void> | Window | null;
  rightIcon?: React.ReactNode;
  surfaceColor?: string;
  target?: string;
  color: ButtonColor;
  variant: ButtonVariant;
  wrap?: boolean;
  shimmer?: boolean; // New optional prop for shimmer effect
}

export const Button = withClickAnalytics(
  ({
    ariaLabel,
    as,
    disabled = false,
    children,
    topClassName,
    className,
    leftIcon,
    loading = false,
    loadingLabel,
    fullWidth = false,
    href,
    onClick,
    rightIcon,
    surfaceColor = "bg-deep_blue-200",
    target,
    color,
    variant,
    wrap,
    shimmer = false, // Default to false
  }: ButtonProps) => {
    const variantStyles = getVariantStyles(variant, color);

    const handleClick = async (
      event:
        | React.KeyboardEvent<HTMLButtonElement | HTMLAnchorElement>
        | React.MouseEvent<HTMLButtonElement | HTMLAnchorElement, MouseEvent>
    ) => {
      if (disabled) {
        event.preventDefault();
        return;
      }
      if (onClick) {
        try {
          await onClick(event);
        } catch (error) {
          console.error("Error in onClick handler:", error);
        }
      }
    };

    const buttonContent = (
      <div
        className={twMerge(
          "relative inline-block rounded",
          disabled && "opacity-50 cursor-not-allowed",
          fullWidth && "w-full",
          variant === "3d" && "-bottom-[3px]"
        )}
      >
        {variant === "3d" && (
          <>
            {/* Base layer */}
            <div
              className={twMerge(
                "absolute h-[52px] inset-0 rounded",
                variantStyles?.base
              )}
            />
          </>
        )}
        {/* Mid layer for 3d functionality */}
        <div
          className={twMerge(
            "relative h-[52px] rounded",
            variant === "3d"
              ? "h-[48px] transition-all duration-100 ease-in-out transform -translate-y-[4px]"
              : variant === "text" && "h-fit",
            variant === "3d" && color === "secondary" && surfaceColor,
            !disabled && "group-active:translate-y-[0]"
          )}
        >
          {/* Top layer */}
          <div
            className={twMerge(
              "h-full w-fit flex items-center justify-center rounded px-12 text-base font-bold transition-all",
              variantStyles?.top,
              variant === "ghost" && "px-4",
              variant === "text" && "p-0",
              topClassName,
              fullWidth && "w-full",
              shimmer && "shimmer-effect relative overflow-hidden"
            )}
          >
            {loading ? (
              <div className="flex items-center gap-2 justify-center">
                <LoadingSpinner />
                {loadingLabel}
              </div>
            ) : (
              <div
                className={twMerge(
                  "w-full flex items-center justify-center",
                  (rightIcon || leftIcon) && !fullWidth
                    ? "justify-between"
                    : "gap-2",
                  !wrap && "whitespace-nowrap"
                )}
              >
                {leftIcon || null}
                {children}
                {rightIcon || null}
              </div>
            )}
          </div>
        </div>
      </div>
    );

    const commonProps = {
      className: twMerge(
        "inline-block group w-fit transition-all",
        className,
        fullWidth && "w-full"
      ),
      "aria-label": ariaLabel,
      "aria-disabled": disabled,
    };

    if (href && !disabled) {
      return (
        <Link
          href={href}
          as={as}
          onClick={disabled ? undefined : handleClick}
          target={target}
          {...commonProps}
        >
          {buttonContent}
        </Link>
      );
    }

    return (
      <button
        disabled={disabled}
        onClick={disabled ? undefined : handleClick}
        {...commonProps}
      >
        {buttonContent}
      </button>
    );
  }
);
